import React, { useEffect, useState } from 'react';
import AppointmentScheduler from './components/AppointmentScheduler';
import LocationSelector from './components/LocationSelector';
import { TireWorksHDConfig, DealerLocation } from './types';
import './App.css';
import './styles/datepicker.css';

interface WidgetConfig {
  dealer: {
    id: string;
    name: string;
    useThirdPartyIntegration: boolean;
    thirdPartyProvider?: string;
    allowedServices?: string[];
    customization: {
      primaryColor: string;
      fontFamily: string;
    };
  };
  locations: DealerLocation[];
  thirdPartyCredentials?: {
    apiEndpoint: string;
    locationId: string;
    applicationUID: string;
  };
}

function App() {
  const [config, setConfig] = useState<TireWorksHDConfig | null>(null);
  const [widgetConfig, setWidgetConfig] = useState<WidgetConfig | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<DealerLocation | null>(null);
  const [loading, setLoading] = useState(true);

  // Load dealer configuration using secure token
  const loadDealerConfigByToken = async (token: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://scheduler-api.ndproductsandbox.com/api';
      
      // First, try the new config endpoint
      const configResponse = await fetch(`${apiUrl}/embed/config/${token}`);
      const configData = await configResponse.json();
      
      if (configData.success && configData.data) {
        const widgetConfigData: WidgetConfig = configData.data;
        setWidgetConfig(widgetConfigData);

        // Create TireWorksHDConfig for backwards compatibility
        const dealerConfig: TireWorksHDConfig = {
          embedToken: token
        };

        // If third-party integration is enabled, include credentials
        if (widgetConfigData.dealer.useThirdPartyIntegration && widgetConfigData.thirdPartyCredentials) {
          dealerConfig.apiEndpoint = widgetConfigData.thirdPartyCredentials.apiEndpoint;
          dealerConfig.locationId = widgetConfigData.thirdPartyCredentials.locationId;
          dealerConfig.applicationUID = widgetConfigData.thirdPartyCredentials.applicationUID;
        }

        // Pass the dealer's service visibility list to the widget
        if (widgetConfigData.dealer.allowedServices && widgetConfigData.dealer.allowedServices.length > 0) {
          dealerConfig.allowedServices = widgetConfigData.dealer.allowedServices;
        }

        setConfig(dealerConfig);

        // Auto-select location if dealer has only one location
        if (widgetConfigData.locations.length === 1) {
          setSelectedLocation(widgetConfigData.locations[0]);
        }
      } else {
        console.warn('New config endpoint failed, falling back to legacy endpoint');
        await loadLegacyConfig(token);
      }
    } catch (error) {
      console.error('Error loading dealer configuration:', error);
      // Fall back to legacy endpoint
      await loadLegacyConfig(token);
    } finally {
      setLoading(false);
    }
  };

  // Legacy configuration loading (for backwards compatibility)
  const loadLegacyConfig = async (token: string) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://scheduler-api.ndproductsandbox.com/api';
      const response = await fetch(`${apiUrl}/embed/credentials/${token}`);
      const data = await response.json();
      
      if (data.success && data.data) {
        const dealerConfig: TireWorksHDConfig = {
          embedToken: token,
          apiEndpoint: data.data.apiEndpoint,
          // Prefer new fields; fall back to legacy if present
          locationId: data.data.locationId || data.data.dealershipId,
          applicationUID: data.data.applicationUID || data.data.accessKey
        };
        setConfig(dealerConfig);
      } else {
        console.error('Failed to load dealer configuration:', data.error);
        // Fall back to demo config with embed token
        setConfig({
          embedToken: token
        });
      }
    } catch (error) {
      console.error('Error loading dealer configuration:', error);
      // Fall back to demo config with embed token
      setConfig({
        embedToken: token
      });
    }
  };

  useEffect(() => {
    // Extract configuration from URL parameters (for embedded mode)
    const urlParams = new URLSearchParams(window.location.search);
    
    const embedToken = urlParams.get('token');
    const dealershipId = urlParams.get('dealership_id'); // Legacy
    const accessKey = urlParams.get('access_key'); // Legacy
    const groupToken = urlParams.get('group_token'); // Legacy
    
    // Modern token-based authentication
    if (embedToken) {
      loadDealerConfigByToken(embedToken);
    } 
    // Legacy parameter support (deprecated)
    else if (dealershipId && accessKey && groupToken) {
      console.warn('Using deprecated URL parameter authentication. Please update to use embed tokens.');
      const embeddedConfig: TireWorksHDConfig = {
        embedToken: 'legacy_token',
        apiEndpoint: 'https://api.tireworkshd.com/webservices',
        dealershipId,
        accessKey,
        groupToken
      };
      setConfig(embeddedConfig);
      setLoading(false);
    } 
    // Default configuration for demo/development
    else {
      const defaultConfig: TireWorksHDConfig = {
        embedToken: 'tws_demo_12345' // Use the demo token from our backend
      };
      setConfig(defaultConfig);
      setLoading(false);
    }

    // Apply theme customizations from URL parameters (legacy support)
    const primaryColor = urlParams.get('primary_color');
    const fontFamily = urlParams.get('font_family');
    
    if (primaryColor) {
      document.documentElement.style.setProperty('--primary-color', primaryColor);
    }
    
    if (fontFamily) {
      document.documentElement.style.setProperty('--font-family', fontFamily);
    }

    // Handle iframe height communication for embedded mode
    const isEmbedded = urlParams.get('embed') === 'true';
    if (isEmbedded && window.parent !== window) {
      const sendHeight = () => {
        const height = document.documentElement.scrollHeight;
        window.parent.postMessage({
          type: 'scheduler-height',
          height: height
        }, '*');
      };

      // Send initial height
      setTimeout(sendHeight, 100);

      // Send height on resize
      window.addEventListener('resize', sendHeight);
      
      // Observe DOM changes to update height
      const observer = new MutationObserver(sendHeight);
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true
      });

      return () => {
        window.removeEventListener('resize', sendHeight);
        observer.disconnect();
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply theme customizations from widget config when available
  useEffect(() => {
    if (widgetConfig) {
      const { primaryColor, fontFamily } = widgetConfig.dealer.customization;
      
      if (primaryColor) {
        document.documentElement.style.setProperty('--primary-color', primaryColor);
      }
      
      if (fontFamily) {
        document.documentElement.style.setProperty('--font-family', fontFamily);
      }
    }
  }, [widgetConfig]);

  if (loading || !config) {
    return (
      <div className="App">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column'
        }}>
          <div className="spinner" style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e9ecef',
            borderTop: '4px solid #667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ marginTop: '20px', color: '#6c757d' }}>Loading scheduler...</p>
        </div>
      </div>
    );
  }

  // Show location selector if dealer has multiple locations and none is selected
  if (widgetConfig && widgetConfig.locations.length > 1 && !selectedLocation) {
    return (
      <div className="App">
        <LocationSelector
          dealerName={widgetConfig.dealer.name}
          locations={widgetConfig.locations}
          onLocationSelect={setSelectedLocation}
        />
      </div>
    );
  }

  return (
    <div className="App">
      <AppointmentScheduler 
        config={config} 
        widgetConfig={widgetConfig || undefined}
        selectedLocation={selectedLocation || undefined}
      />
    </div>
  );
}

export default App;